import * as Sentry from "@sentry/vue";
import type { UserDto } from "@umi/api/schemas/user";
import { enrichSentryEvent } from "@/utils/sentry";


export default defineNuxtPlugin((nuxtApp) => {

  const config = useRuntimeConfig();
  const router = useRouter();
  const authStore = useAuthStore();

  if (!["edge", "prod", "dev"].includes(config.public.stage)) return;

  Sentry.init({
    app: nuxtApp.vueApp,
    dsn: config.public.sentryDsn,
    environment: config.public.stage,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
    ],
    tracesSampleRate: ["prod", "edge"].includes(config.public.stage) ? 0.2 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      // Filter 401s from oRPC (`/orpc/`) and the chain RPC proxy (`/rpc/`).
      // viem wraps proxy errors several levels deep, so every exception value is checked.
      const values = event.exception?.values ?? [];
      const isRpcAuthError = values.some(v =>
        v.value?.includes("Status: 401")
        && (v.value?.includes("/orpc/") || v.value?.includes("/rpc/"))
      );
      if (isRpcAuthError) return null;
      return enrichSentryEvent(event, hint);
    },
  });


  const setUser = (user: UserDto | null) => {
    if (user) {
      Sentry.setUser({
        userId: user.id,
        discordId: user.discordId,
        discordUsername: user.discordUsername,
        accountAddress: user.accountAddress,
      });
    } else Sentry.setUser(null);
  };

  watch(
    () => authStore.user,
    (newUser) => { setUser(newUser); },
    { immediate: true }
  );

  return { provide: { sentry: Sentry } };
});
